import React, { useState } from 'react';

import { RoundedCornerOutlined } from '@mui/icons-material';
import { Palette, Type } from 'lucide-react';

import EmailLayoutPropsSchema, {
  EmailLayoutProps,
} from '../../../../documents/blocks/EmailLayout/EmailLayoutPropsSchema';

import BaseSidebarPanel, { SectionHeader } from './helpers/BaseSidebarPanel';
import ColorInput, { NullableColorInput } from './helpers/inputs/ColorInput';
import { NullableFontFamily } from './helpers/inputs/FontFamily';
import SliderInput from './helpers/inputs/SliderInput';
import { Separator } from '@/components/ui/separator';

type EmailLayoutSidebarFieldsProps = {
  data: EmailLayoutProps;
  setData: (v: EmailLayoutProps) => void;
};

export default function EmailLayoutSidebarFields({ data, setData }: EmailLayoutSidebarFieldsProps) {
  const [, setErrors] = useState<Zod.ZodError | null>(null);

  const updateData = (d: unknown) => {
    const res = EmailLayoutPropsSchema.safeParse(d);
    if (res.success) {
      setData(res.data);
      setErrors(null);
    } else {
      setErrors(res.error);
    }
  };

  return (
    <BaseSidebarPanel title="Global Styles">
      {/* COLORS SECTION */}
      <div className="space-y-4">
        <SectionHeader icon={Palette}>Colors</SectionHeader>
        <div className="space-y-3">
          <ColorInput
            label="Backdrop color"
            defaultValue={data.backdropColor ?? '#F5F5F5'}
            onChange={(backdropColor) => updateData({ ...data, backdropColor })}
          />
          <ColorInput
            label="Canvas color"
            defaultValue={data.canvasColor ?? '#FFFFFF'}
            onChange={(canvasColor) => updateData({ ...data, canvasColor })}
          />
          <NullableColorInput
            label="Canvas border color"
            defaultValue={data.borderColor ?? null}
            onChange={(borderColor) => updateData({ ...data, borderColor })}
          />
        </div>
      </div>

      <Separator className="my-6" />

      {/* LAYOUT SECTION */}
      <div className="space-y-4">
        <SectionHeader>Layout</SectionHeader>
        <div className="space-y-3">
          <SliderInput
            iconLabel={<RoundedCornerOutlined />}
            units="px"
            step={4}
            marks
            min={0}
            max={48}
            label="Canvas border radius"
            defaultValue={data.borderRadius ?? 0}
            onChange={(borderRadius) => updateData({ ...data, borderRadius })}
          />
        </div>
      </div>

      <Separator className="my-6" />

      {/* TYPOGRAPHY SECTION */}
      <div className="space-y-4">
        <SectionHeader icon={Type}>Typography</SectionHeader>
        <div className="space-y-3">
          <NullableFontFamily
            label="Font family"
            defaultValue="MODERN_SANS"
            onChange={(fontFamily) => updateData({ ...data, fontFamily })}
          />
          <ColorInput
            label="Text color"
            defaultValue={data.textColor ?? '#262626'}
            onChange={(textColor) => updateData({ ...data, textColor })}
          />
        </div>
      </div>
    </BaseSidebarPanel>
  );
}
